import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";

const TrialContext = createContext(null);

export const useTrial = () => {
  const context = useContext(TrialContext);
  if (!context) {
    throw new Error("useTrial must be used within TrialProvider");
  }
  return context;
};

export const TrialProvider = ({ children }) => {
  const { api, user, isAuthenticated } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTrialStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/trial/status");
      setTrialStatus(res.data);
    } catch {
      setTrialStatus(null);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated]);

  const fetchChecklist = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get("/auth/onboarding-checklist");
      setChecklist(res.data.checklist);
    } catch {
      setChecklist(null);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    fetchTrialStatus();
    fetchChecklist();
  }, [fetchTrialStatus, fetchChecklist]);

  const completeChecklistStep = async (step) => {
    try {
      await api.post(`/auth/onboarding-checklist/${step}`);
      setChecklist((prev) => (prev ? { ...prev, [step]: true } : prev));
    } catch (err) {
      console.error("Failed to update checklist:", err);
    }
  };

  const checkTrialLimit = async (resourceType) => {
    if (!trialStatus?.is_trial) return { allowed: true };
    try {
      const res = await api.get(`/trial/check-limit/${resourceType}`);
      return res.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return { allowed: false, message: err.response.data.detail };
      }
      return { allowed: true };
    }
  };

  const triggerUpgradeWall = (resourceLabel, current, limit) => {
    setUpgradeContext({ resourceLabel, current, limit });
    setShowUpgradeModal(true);
  };

  const dismissUpgradeModal = () => {
    setShowUpgradeModal(false);
    setUpgradeContext(null);
  };

  const isTrial = trialStatus?.is_trial ?? false;
  const isDemo = trialStatus?.plan_type === "demo" || user?.plan_type === "demo";
  const isExpired = trialStatus?.trial_expired ?? false;
  const isReadOnly = trialStatus?.read_only ?? false;
  const isFrozen = trialStatus?.frozen ?? false;
  const daysRemaining = trialStatus?.days_remaining ?? 0;
  const daysElapsed = trialStatus?.days_elapsed ?? 0;
  const usage = trialStatus?.usage ?? {};
  const limits = trialStatus?.limits ?? {};

  const value = {
    trialStatus,
    checklist,
    isTrial,
    isDemo,
    isExpired,
    isReadOnly,
    isFrozen,
    daysRemaining,
    daysElapsed,
    usage,
    limits,
    loading,
    showUpgradeModal,
    upgradeContext,
    fetchTrialStatus,
    fetchChecklist,
    completeChecklistStep,
    checkTrialLimit,
    triggerUpgradeWall,
    dismissUpgradeModal,
  };

  return (
    <TrialContext.Provider value={value}>{children}</TrialContext.Provider>
  );
};
